import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getCurrencyValue } from "./number-service";

export const handleExportToPDF = (analysisResultId) => {
  let reportData = JSON.parse(analysisResultId?.reportData || "{}");
  let fleet = [reportData?.fleet || {}];
  let fleetMix = [...(reportData?.fleetMix || [])];
  let maintenance = [reportData?.maintainanceCost || {}];
  let cycling = [reportData?.cyclingComparision || {}];
  let fuelCost = [reportData?.fuelCost || {}];

  const formatCurrency = (val) => {
    if (val === undefined || val === null) return "";
    return "$" + Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatNumber = (val, decimals = 0) => {
    if (val === undefined || val === null) return "";
    return Number(val).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const doc = new jsPDF({ orientation: "landscape" });
  let currentY = 15;

  doc.setFontSize(18);
  doc.text(`Analysis Report: ${analysisResultId?.reportName || "Report"}`, 14, currentY);
  currentY += 10;

  const drawVerticalTable = (title, headers, dataArray, startY, isCurrencyMap = {}) => {
      const body = dataArray.map(item => {
          return headers.map(header => {
              let val = item[header];
              if (isCurrencyMap[header]) {
                  val = formatCurrency(val);
              } else if (typeof val === "number") {
                   val = formatNumber(val, val % 1 === 0 ? 0 : 2);
              }
              return val;
          });
      });

      doc.setFontSize(14);
      doc.text(title, 14, startY);
      
      autoTable(doc, {
          startY: startY + 4,
          head: [headers],
          body: body,
          theme: 'grid',
          headStyles: { fillColor: [141, 180, 226] },
          styles: { fontSize: 9, cellPadding: 2 }
      });
      return doc.lastAutoTable.finalY + 10;
  };

  const fleetHeaders = ["Current Fleet Size", "Avg. Vehicle Model Year", "Avg. New Vehicle Price", "Avg. Current Mileage", "Current Cycles", "Avg. Annual Miles"];
  const fleetData = fleet.map(item => ({
      "Current Fleet Size": item?.currentFleetSize,
      "Avg. Vehicle Model Year": item?.avgVehicleYear,
      "Avg. New Vehicle Price": item?.avgNewVehiclePrice,
      "Avg. Current Mileage": Math.round(item?.currentMileage || 0),
      "Current Cycles": item?.currentCycle,
      "Avg. Annual Miles": Math.round(item?.annualMiles || 0),
  }));
  currentY = drawVerticalTable("Fleet", fleetHeaders, fleetData, currentY, { "Avg. New Vehicle Price": true });

  const maintHeaders = ["Total Annualized Spend", "Per Vehicle Spend", "Vehicle Spend/Month", "Avg. Cost Per Mile Per Vehicle Spend"];
  const maintData = maintenance.map(item => ({
      "Total Annualized Spend": item?.totalAnnualized,
      "Per Vehicle Spend": item?.vehicleSpend,
      "Vehicle Spend/Month": item?.vehicleSpendMonth,
      "Avg. Cost Per Mile Per Vehicle Spend": item?.costPerMile,
  }));
  currentY = drawVerticalTable("Maintenance Cost", maintHeaders, maintData, currentY, {
      "Total Annualized Spend": true, "Per Vehicle Spend": true, "Vehicle Spend/Month": true, "Avg. Cost Per Mile Per Vehicle Spend": true
  });

  const fuelHeaders = ["Avg. Mpg", "Avg. Annual Miles", "Number Of Vehicle", "Cost Per Gallon", "Total Fuel Spend", "Annual Spend Vehicle", "Monthly Spend Vehicle"];
  const fuelCostData = fuelCost.map(item => ({
      "Annual Spend Vehicle": item?.annualSpendVehicle,
      "Avg. Annual Miles": Math.round(item?.avgAnnualMiles || 0),
      "Avg. Mpg": item?.avgMpg,
      "Cost Per Gallon": item?.costPerGallon,
      "Monthly Spend Vehicle": item?.monthlySpendVehicle,
      "Number Of Vehicle": item?.numberOfVehicle,
      "Total Fuel Spend": item?.totalFuelSpend,
  }));
  if (currentY > 160) {
      doc.addPage();
      currentY = 15;
  }
  currentY = drawVerticalTable("Fuel Cost", fuelHeaders, fuelCostData, currentY, {
      "Cost Per Gallon": true, "Total Fuel Spend": true, "Annual Spend Vehicle": true, "Monthly Spend Vehicle": true
  });

  const cyclingHeaders = ["ProposedCycle", "Monthly Lease Cost", "Avg. Year 1 MPG", "Fuel Spend Year 1", "Monthly Maintanance Year1", "Avg. Year 1 Cost Per Miles"];
  const cyclingData = cycling.map(item => ({
      "Avg. Year 1 Cost Per Miles": item?.avgYear1CostPerMiles,
      "Avg. Year 1 MPG": item?.avgYear1Mpg,
      "Fuel Spend Year 1": item?.fuelSpendY1,
      "Monthly Lease Cost": item?.monthlyLeaseCost,
      "Monthly Maintanance Year1": item?.monthlyMaintananceYear1,
      "ProposedCycle": item?.proposedCycle,
  }));
  if (currentY > 160) {
      doc.addPage();
      currentY = 15;
  }
  currentY = drawVerticalTable("Cycling Comparison", cyclingHeaders, cyclingData, currentY, {
      "Monthly Lease Cost": true, "Fuel Spend Year 1": true, "Monthly Maintanance Year1": true, "Avg. Year 1 Cost Per Miles": true
  });

  // Fleet Mix
  const mixHeaders = ["Fiscal Year", "Fleet Size", "Annual Needs", "Purchased/Financed", "Cycling Payment", "Cash Outlay", "Current Payment", "Maintenance", "Fuel", "Fleet Spend", "Vehicle Equity", "Savings"];
  const mixData = fleetMix.map(item => ({
      "Fiscal Year": item?.fiscalYear,
      "Fleet Size": item?.fleetSize,
      "Annual Needs": item?.annualNeeds,
      "Purchased/Financed": item?.purchasedFinancial,
      "Cycling Payment": item?.cyclingPayment,
      "Cash Outlay": item?.cashOutlay,
      "Current Payment": item?.currentPayment,
      "Maintenance": item?.maintenance,
      "Fuel": item?.fuel,
      "Fleet Spend": item?.fleetSpeed,
      "Vehicle Equity": item?.vehicleEquity,
      "Savings": item?.savings,
  }));
  if (currentY > 150) {
      doc.addPage();
      currentY = 15;
  }
  
  doc.setFontSize(14);
  doc.text("Fleet Mix", 14, currentY);
  
  // Calculate total savings
  const totalSavings = fleetMix.reduce((sum, item) => sum + (item.savings || 0), 0);
  
  autoTable(doc, {
      startY: currentY + 4,
      head: [mixHeaders],
      body: mixData.map(item => {
          return mixHeaders.map(h => {
              if (["Cycling Payment", "Cash Outlay", "Current Payment", "Maintenance", "Fuel", "Fleet Spend", "Vehicle Equity", "Savings"].includes(h)) {
                  return formatCurrency(item[h]);
              }
              return item[h];
          });
      }),
      theme: 'grid',
      headStyles: { fillColor: [141, 180, 226] },
      styles: { fontSize: 8, cellPadding: 2 },
      didDrawPage: function (data) {
        // Nothing for now
      }
  });

  // Add Total Savings below Fleet Mix table
  currentY = doc.lastAutoTable.finalY + 10;
  if (currentY > 180) {
      doc.addPage();
      currentY = 15;
  }
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(`Total Savings: ${formatCurrency(totalSavings)}`, 14, currentY);

  doc.save(`${analysisResultId?.reportName || "Report"}.pdf`);
};
